import * as React from "react";
import { cn } from "@uconnect/assets/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import Icon from "@uconnect/assets/components/ui/icon";
type SearchInputProps = React.ComponentProps<typeof Input> & {
	iconClassName?: string;
	wrapperClassName?: string;
};

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
	({ iconClassName, wrapperClassName, ...props }, ref) => {
		return (
			<div className={cn("relative", wrapperClassName)}>
				<Input
					{...props}
					ref={ref}
					className={cn("!pl-8", props.className)}
				/>
				<Icon
					name="search"
					className={cn('absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground leading-1 w-4 h-4 z-10', iconClassName)}
					aria-hidden="true"
				/>
			</div>
		);
	}
);
SearchInput.displayName = "SearchInput";

const inputVariants = cva(
	"file:text-foreground placeholder:text-muted-foreground! selection:bg-primary selection:text-primary-foreground w-full min-w-0 !rounded-md border transition-[color] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive !min-h-0",
	{
		variants: {
			variant: {
				default: "!border-border bg-white",
				subtle: "border-none bg-muted/40",
			},
			size: {
				default: "h-9 !text-base !px-3 !py-1",
				sm: "h-8 !text-sm !px-2 !py-1",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

type InputProps = Omit<React.ComponentProps<"input">, "size"> &
	VariantProps<typeof inputVariants> & {
		className?: string;
	};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, type, variant, size, ...props }, ref) => {
		return (
			<input
				type={type}
				data-slot="input"
				ref={ref}
				className={cn(inputVariants({ variant, size }), className)}
				{...props}
			/>
		);
	}
);
Input.displayName = "Input";

export { Input, SearchInput };
